/**
 * @module free-shipping-bar
 *
 * Cart-drawer Shipping Progress Bar.
 *
 * Reads the per-market free-shipping threshold from the #FreeShippingPromo
 * element's data attributes and renders "X away from free standard shipping"
 * / "You qualify for free shipping" as the cart changes.
 *
 * Additionally mirrors the `df-vendor-free-shipping` Shopify Function: when
 * the cart contains a line item from a configured vendor (e.g. The Chemistry
 * Brand) AND the shopper's market is eligible, it short-circuits to the
 * qualified state regardless of cart spend — so the drawer message never
 * disagrees with the discount applied at checkout.
 *
 * Exposes `window.updateFreeShippingBar([cartData])` for other modules
 * (e.g. shared-cart) to trigger a re-render.
 */

import { formatCurrency } from './format.js';
import { qualifiesForVendorFreeShipping } from './eligibility.js';
import type { Cart } from './eligibility.js';

const QUALIFIED_MESSAGE = 'You qualify for free shipping';
type ShippingCart = Cart & { total_price?: number };

function getThresholdAndCurrency(): { threshold: number; currency: string } {
  const promo = document.getElementById('FreeShippingPromo');
  if (!promo) return { threshold: 100, currency: 'USD' }; // default config

  // Parse threshold from text (handles various formats)
  const thresholdText = promo.getAttribute('data-threshold') || '100';
  // Remove any non-numeric characters except decimal point
  const cleanedText = thresholdText.replace(/[^\d.]/g, '');
  const threshold = parseFloat(cleanedText);

  // threshold of 0 is valid and intentional: free shipping on all orders for
  // that market. Only reject unparseable (NaN) or negative values.
  if (isNaN(threshold) || threshold < 0) {
    console.warn('[FreeShipping] Invalid threshold:', thresholdText);
    return { threshold: 100, currency: 'USD' };
  }

  const currency = promo.getAttribute('data-currency') || 'USD';

  return { threshold, currency };
}

/**
 * Updates the free shipping progress bar based on cart total.
 * NOTE: Shopify returns all monetary values * 100, even for zero-decimal currencies.
 * @param {Object} cart - Cart object from Shopify's /cart.js API
 */
function updateFreeShipping(cart: ShippingCart): void {
  const promo = document.getElementById('FreeShippingPromo');
  if (!promo) return;

  const messageEl = promo.querySelector<HTMLElement>('.shipping-message');
  const progressEl = promo.querySelector<HTMLElement>('.progress-fill');

  if (!messageEl || !progressEl) return;

  // Vendor free-shipping short-circuit: a qualifying vendor line in an
  // eligible market grants free shipping regardless of spend.
  if (qualifiesForVendorFreeShipping(cart)) {
    messageEl.textContent = QUALIFIED_MESSAGE;
    progressEl.style.width = '100%';
    return;
  }

  // Get threshold and currency from data attributes
  const { threshold, currency } = getThresholdAndCurrency();

  // Shopify always returns total_price * 100, even for zero-decimal currencies
  // (e.g., 1000 KRW is returned as 100000)
  const FREE_SHIPPING_THRESHOLD = threshold * 100;

  // Cart total in minor units, after discounts — matches the after-discount
  // basis of the free-shipping shipping rate, so applying a code moves the bar.
  const cartTotal = cart.total_price || 0;
  const remaining = FREE_SHIPPING_THRESHOLD - cartTotal;

  if ((window as Window & { DEBUG_FREE_SHIPPING?: boolean }).DEBUG_FREE_SHIPPING) {
    console.log('[FreeShipping]', {
      currency,
      threshold,
      FREE_SHIPPING_THRESHOLD,
      cartTotal,
      remaining,
    });
  }

  if (remaining > 0) {
    // Convert from minor units back to major units for display
    const remainingInCurrency = remaining / 100;
    const remainingFormatted = formatCurrency(remainingInCurrency, currency);

    messageEl.textContent = `${remainingFormatted} away from free standard shipping`;

    const progress = Math.min((cartTotal / FREE_SHIPPING_THRESHOLD) * 100, 100);

    progressEl.style.width = `${progress}%`;
  } else {
    messageEl.textContent = QUALIFIED_MESSAGE;
    progressEl.style.width = '100%';
  }
}

function getCartAndUpdate(): Promise<void> {
  const root = window.Shopify?.routes?.root || '/';
  return fetch(root + 'cart.js')
    .then((res) => res.json())
    .then((cart: ShippingCart) => updateFreeShipping(cart))
    .catch((err) => console.error('Free shipping: Cart fetch error', err));
}

function setupFetchInterceptor(): void {
  const originalFetch = window.fetch;
  window.fetch = function (...args) {
    const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request | undefined)?.url;

    return originalFetch.apply(this, args).then((response) => {
      if (
        url &&
        (url.includes('/cart/add') ||
          url.includes('/cart/change') ||
          url.includes('/cart/update') ||
          url.includes('/cart/clear'))
      ) {
        response
          .clone()
          .json()
          .then((data) => {
            setTimeout(() => {
              // /cart/change|update|clear.js return the FULL cart (with
              // total_price) — render straight from it. /cart/add.js returns
              // only the added line items ({ items: [...] }, no total_price),
              // so rendering on it would treat the cart as $0; fall back to a
              // full /cart.js fetch. Presence of total_price is the signal.
              if (data && data.total_price !== undefined) {
                updateFreeShipping(data);
              } else {
                void getCartAndUpdate();
              }
            }, 100);
          })
          .catch(() => {
            setTimeout(getCartAndUpdate, 100);
          });
      }

      return response;
    });
  };
}

function init(): void {
  // Make the update function globally accessible (e.g. for shared-cart).
  window.updateFreeShippingBar = function (cartData) {
    if (cartData) {
      updateFreeShipping(cartData as ShippingCart);
    } else {
      void getCartAndUpdate();
    }
  };

  // Initial load — skip when the bar isn't on the page (e.g. empty cart, where
  // cart-drawer.liquid doesn't render #FreeShippingPromo). Reactive updates
  // (interceptor, cart events) re-query the element as it appears.
  if (document.getElementById('FreeShippingPromo')) {
    void getCartAndUpdate();
  }

  // Intercept fetch to cart APIs
  setupFetchInterceptor();

  // Backup listeners
  document.addEventListener('cart-drawer:open', getCartAndUpdate);
  document.addEventListener('cart:updated', getCartAndUpdate);

  // Listen for market/currency changes
  document.addEventListener('localization:changed', function () {
    setTimeout(getCartAndUpdate, 200);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

export { init, updateFreeShipping, getCartAndUpdate, getThresholdAndCurrency };
